import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils';

const DOT_VARIANTS = new Set(['active', 'pending', 'processing']);

/**
 * A state's appearance. Which state a domain object is in, and its label,
 * belong to the domain's own presentation module; this only draws it.
 * Live states (active, pending, processing) carry a dot.
 */
export const statusBadgeVariants = cva(
  'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full font-semibold',
  {
    variants: {
      variant: {
        active: 'bg-success-muted text-success',
        success: 'bg-success-muted text-success',
        processing: 'bg-info-muted text-info',
        info: 'bg-info-muted text-info',
        pending: 'bg-muted text-muted-foreground',
        draft: 'bg-muted text-muted-foreground',
        disabled: 'bg-muted text-muted-foreground',
        unknown: 'bg-muted text-muted-foreground',
        warning: 'bg-warning-muted text-warning',
        error: 'bg-danger-muted text-danger',
      },
      size: {
        default: 'px-2 py-0.5 text-xs',
        sm: 'px-1.5 py-px text-caption',
      },
    },
    defaultVariants: {
      variant: 'unknown',
      size: 'default',
    },
  }
);

export interface StatusBadgeProps
  extends React.ComponentPropsWithoutRef<'span'>,
    VariantProps<typeof statusBadgeVariants> {}

export function StatusBadge({
  variant,
  size,
  className,
  children,
  ...props
}: StatusBadgeProps) {
  return (
    <span className={cn(statusBadgeVariants({ variant, size }), className)} {...props}>
      {DOT_VARIANTS.has(variant ?? 'unknown') ? (
        <span aria-hidden="true" className="size-1.5 rounded-full bg-current" />
      ) : null}
      {children}
    </span>
  );
}
